/*
 * @Author: Ryan
 * @Date: 2025-12-29
 * @Description: Old Way vs FalcoCut comparison section for Voice Cloning
 */
import React from 'react';

import StartButton from './StartButton';

interface ComparisonItem {
  label: string;
  value: string;
}

const oldWayItems: ComparisonItem[] = [
  { label: 'Time Invested', value: 'Hours or days per script' },
  { label: 'Cost of Production', value: '$300–$2,000+ for voice actors and studio time' },
  { label: 'Team Required', value: 'Voice talent, sound engineer, producer' },
  { label: 'Quality', value: 'Inconsistent tone across recordings' },
  {
    label: 'Workflow',
    value:
      'Hiring voice actors → Scheduling sessions → Recording → Re-recording for every script change',
  },
];

const newWayItems: ComparisonItem[] = [
  { label: 'Time Invested', value: 'Just minutes' },
  { label: 'Cost of Production', value: 'From a few dollars per voice output' },
  { label: 'Team Required', value: 'Just you' },
  { label: 'Quality', value: 'Natural, consistent, and reusable voice' },
  {
    label: 'Workflow',
    value: 'Type your script → Pick an avatar → Generate video instantly',
  },
];

const ComparisonSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Old Way vs. FalcoCut Voice Cloning
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-5xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          Skip the high costs, long recording sessions, and constant re-recording of
          traditional voice production. With FalcoCut, professional voice cloning takes
          minutes — no studios, no voice actors, no reshoots.
        </p>

        {/* Comparison Cards */}
        <div className="grid grid-cols-1 pad:grid-cols-2 gap-6 pad:gap-8 mb-10 pad:mb-14">
          {/* Old Way Card */}
          <div className="rounded-2xl border border-gray-200 p-6 pad:p-8">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-xl pad:text-2xl font-bold text-gray-900">Old Way</h3>
            </div>

            {/* List */}
            <ul className="space-y-4 mb-6">
              {oldWayItems.map((item, index) => (
                <li key={index} className="flex">
                  <span className="text-gray-400 mr-2">&bull;</span>
                  <span className="text-sm pad:text-base text-gray-700">
                    <span className="font-medium">{item.label}:</span> {item.value}
                  </span>
                </li>
              ))}
            </ul>

            {/* Summary */}
            <div className="flex items-center gap-2 text-base pad:text-base font-semibold text-gray-800">
              <span>😫</span>
              <span>Slow, expensive, and hard to scale.</span>
            </div>
          </div>

          {/* New Way Card */}
          <div className="rounded-2xl border-2 border-[#7C3AED] bg-[#7C3AED]/5 p-6 pad:p-8">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-full bg-[#7C3AED] flex items-center justify-center">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.5 4.5L6 12L2.5 8.5"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-xl pad:text-2xl font-bold text-gray-900">
                New Way with FalcoCut
              </h3>
            </div>

            {/* List */}
            <ul className="space-y-4 mb-6">
              {newWayItems.map((item, index) => (
                <li key={index} className="flex">
                  <span className="text-[#7C3AED] mr-2">&bull;</span>
                  <span className="text-sm pad:text-base text-gray-700">
                    <span className="font-medium">{item.label}:</span> {item.value}
                  </span>
                </li>
              ))}
            </ul>

            {/* Summary */}
            <div className="flex items-center gap-2 text-base pad:text-base font-semibold text-[#7C3AED]">
              <span>🚀</span>
              <span>Fast, affordable, and built for scale.</span>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div className="flex justify-center">
          <StartButton text="Try Voice Cloning" />
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;
